import React, { useState } from 'react';
import {
  Button,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { makeStyles } from 'tss-react/mui';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { GVTypography } from 'components/lib';
import GVTextField from 'components/lib/GVTextField/GVTextField';
import { copyPrepInspection } from 'store/request/inspections/actions';

const useStyles = makeStyles()(() => ({
  dialogContent: {
    padding: '24px',
    background: 'var(--gds-color-cool-gray-700)',
    '--gds-spacing-field-container-margin': 'var(--gds-spacing-lg) 0',
  },
  dialogTitle: {
    background: 'var(--gds-color-cool-gray-700)',
    paddingX: '24px',
    fontWeight: 600,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '-16px',
  },
}));

interface InspectionCopyPrepModalProps {
  open: boolean;
  onClose: () => void;
  inspectionId: string;
  inspectionName: string;
}

const InspectionCopyPrepModal: React.FC<InspectionCopyPrepModalProps> = ({
  open,
  onClose,
  inspectionId,
  inspectionName,
}) => {
  const { classes } = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [newInspectionName, setNewInspectionName] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  const handleCopyPrep = async () => {
    // Start loading
    setLoading(true);

    try {
      await dispatch(
        copyPrepInspection(
          inspectionId,
          {
            newName: newInspectionName.trim() || undefined,
          },
          (responseData: any) => {
            // Navigate to the prep page of the new inspection
            navigate(`/inspection/${responseData.id}`);
            handleClose();
          },
        ),
      );

      // Reset form after successful copy
      setNewInspectionName('');
    } catch (error) {
      // Error handling is done in the action's onError callback
      console.error('Error copying inspection prep:', error);
    } finally {
      // Always stop loading, regardless of success or error
      setLoading(false);
    }
  };

  const handleClose = () => {
    // Reset loading state and form
    setLoading(false);
    setNewInspectionName('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <Grid sx={{ background: 'var(--gds-color-cool-gray-700)' }} data-testid="create-linked-inspection-modal">
        <DialogTitle className={classes.dialogTitle}>
          <Typography variant="h6">Create Linked Inspection</Typography>
          <IconButton
            id="close-linked-inspection"
            aria-label="close"
            onClick={handleClose}
            size="small"
            disabled={loading}
          >
            <CloseIcon color="primary" />
          </IconButton>
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          <GVTypography variant="body2" color="textPrimary" gutterBottom>
            Create a new inspection linked to this one, copying the prep data (zones, crops, settings) without the
            results. All linked inspections can be included together in a single report.
          </GVTypography>

          <GVTypography variant="body2" color="textSecondary" sx={{ marginTop: 2 }}>
            <strong>Source:</strong> {inspectionName}
          </GVTypography>

          <GVTextField
            label="New Inspection Name (Optional)"
            value={newInspectionName}
            onValueChange={(value) => setNewInspectionName(value ?? '')}
            placeholder="Enter a new name for the inspection"
            message='If left empty, will use "(Copy Prep) [original name]"'
            isDisabled={loading}
            id="new-inspection-name-input"
          />
        </DialogContent>

        <DialogActions sx={{ padding: '0 24px 24px 24px' }}>
          <Button data-testid="cancel-linked-inspection" onClick={handleClose} color="secondary" disabled={loading}>
            Cancel
          </Button>
          <Button
            data-testid="create-linked-inspection"
            variant="contained"
            color="primary"
            onClick={handleCopyPrep}
            disabled={loading}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {loading ? 'Creating...' : 'Create Linked Inspection'}
          </Button>
        </DialogActions>
      </Grid>
    </Dialog>
  );
};

export default InspectionCopyPrepModal;
